import { Box, Text } from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0DatePicker } from '@factorialco/f0-react'
import { Input, Select } from '@factorialco/f0-react/dist/experimental'

import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { SubprojectBasicDetails } from 'modules/project_management/components/tables/SubprojectsDataCollection/types'
import { statusOptions } from 'modules/project_management/lib/projectStatus'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

interface SubprojectsSettingsTabProps {
  subprojectId: number
  subprojectData?: ISubprojectsPanelData
  formValues: SubprojectBasicDetails
  formErrors: FormErrors<SubprojectBasicDetails>
  onFieldChange: (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => void
  subprojectDetails: {
    subprojectName?: string
    subprojectId: number
    description?: string
    status: string
    code?: string
    startDate?: string
    dueDate?: string
  }
}

const SubprojectsSettingsTab = ({
  formValues,
  formErrors,
  onFieldChange,
  subprojectDetails,
}: SubprojectsSettingsTabProps) => {
  const statusType = statusOptions({ action: 'create' }).map((status) => ({
    label: status.label,
    value: status.value,
  }))

  const handleFieldChanged = (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => {
    onFieldChange(fieldName, fieldValue)
  }

  const dateSelected = (
    value: DatePickerValue | undefined,
    fieldName: keyof SubprojectBasicDetails
  ) => {
    const dateString = value?.value?.from.toISOString()
    if (dateString) {
      handleFieldChanged(fieldName, dateString)
    }
  }

  const stringToDatePickerValue = (dateString: string | undefined): DatePickerValue | undefined => {
    if (!dateString) return undefined

    const date = new Date(dateString)
    if (isNaN(date.getTime())) return undefined
    const from = new Date(date)
    const to = new Date(date)

    return {
      granularity: 'day',
      value: {
        from,
        to,
      },
    }
  }

  return (
    <Box gap="s16">
      <Text color="textPrimary" size="300" weight="medium">
        {i18n.t('project_management.settings.sections.basic_information.title')}
      </Text>

      <Input
        size="md"
        label={i18n.t('project_management.table.projects_list.headers.name')}
        placeholder={
          subprojectDetails.subprojectName || i18n.t('project_management.export.subproject_name')
        }
        value={formValues.name || ''}
        onChange={(value) => handleFieldChanged('name', value || undefined)}
        error={formErrors.name}
      />
      <Input
        size="md"
        label={i18n.t('project_management.table.projects_list.headers.code')}
        placeholder={'SP01'}
        value={formValues.code || ''}
        onChange={(value) => handleFieldChanged('code', value || undefined)}
        error={formErrors.code}
      />
      <Box flexDirection="row" gap="s16">
        <F0DatePicker
          label={i18n.t('project_management.imports.table.headers.start_date')}
          onChange={(value) => dateSelected(value, 'startDate')}
          placeholder="MM/DD/YYYY"
          value={stringToDatePickerValue(formValues.startDate)}
        />
        <F0DatePicker
          label={i18n.t('project_management.imports.table.headers.due_date')}
          onChange={(value) => dateSelected(value, 'dueDate')}
          placeholder="MM/DD/YYYY"
          value={stringToDatePickerValue(formValues.dueDate)}
        />
      </Box>
      <Select
        label={i18n.t('project_management.imports.table.headers.status')}
        value={formValues.status}
        onChange={(value: string) => handleFieldChanged('status', value)}
        options={statusType}
        error={formErrors.status}
      />
      <Input
        size="md"
        label={i18n.t('project_management.modals.create_project.input_labels.project_description')}
        placeholder={i18n.t(
          'project_management.modals.create_project.input_labels.project_description'
        )}
        value={formValues.description || ''}
        onChange={(value) => handleFieldChanged('description', value || undefined)}
        error={formErrors.description}
      />
    </Box>
  )
}

export default SubprojectsSettingsTab
